import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { type Booking } from '@/hooks/useBookings';
import { parseDateFromDatabase } from '@/utils/dateParser';
import { formatCurrency } from '@/utils/shared/currencyUtils';
import { truncateText } from '@/utils/shared/stringUtils';
import { format as formatDate } from 'date-fns';
import { es } from 'date-fns/locale';
import { Copy, Edit } from 'lucide-react';

interface BookingRowProps {
  booking: Booking;
  onEdit: (booking: Booking) => void;
  onCopy: (id: string) => void;
  onCancel: (booking: Booking) => void;
  onActivate: (booking: Booking) => void;
  isLoading: boolean;
}

const BookingRow = ({
  booking,
  onEdit,
  onCopy,
  onCancel,
  onActivate,
  isLoading,
}: BookingRowProps) => {
  const formatBookingDate = (dateString: string) => {
    const date = parseDateFromDatabase(dateString);
    if (!date) return dateString;
    return formatDate(date, 'dd/MM/yy', { locale: es });
  };

  const getStayCompletedBadge = (stayCompleted: boolean | null) =>
    stayCompleted ? (
      <Badge className="bg-green-100 hover:bg-green-200 text-green-800">
        Sí
      </Badge>
    ) : (
      <Badge variant="destructive">No</Badge>
    );

  const getPaymentStatusBadge = (paymentStatus: string | null) => {
    switch (paymentStatus) {
      case 'paid':
        return (
          <Badge className="bg-green-100 hover:bg-green-100 text-green-800">
            Pagado
          </Badge>
        );
      case 'partial':
        return (
          <Badge className="bg-amber-100 hover:bg-amber-100 text-amber-800">
            Pago parcial
          </Badge>
        );
      case 'pending':
      default:
        return (
          <Badge variant="destructive">
            Pendiente
          </Badge>
        );
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Reservada':
        return (
          <Badge className="bg-green-100 hover:bg-green-200  text-green-800">
            Reservada
          </Badge>
        );
      case 'Cancelada':
        return <Badge variant="destructive">Cancelada</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <TableRow key={booking.id} className="hover:bg-gray-50">
      <TableCell className="px-4 py-4">
        <div className="text-sm font-medium text-gray-900">{booking.booking_id}</div>
        <div className="text-sm text-gray-600">{booking.guest_name}</div>
        {booking.guest_email && (
          <div className="text-gray-500 text-xs">{booking.guest_email}</div>
        )}
      </TableCell>

      <TableCell className="px-4 py-4 text-sm text-gray-900">
        <span className="font-medium" title={booking.property_short_name}>
          {truncateText(booking.property_short_name, 20)}
        </span>
      </TableCell>

      <TableCell className="px-4 py-4 text-sm text-gray-900">
        {booking.origins?.name || 'N/A'}
      </TableCell>

      <TableCell className="px-4 py-4 text-sm text-gray-900">
        {formatBookingDate(booking.check_in)}
      </TableCell>

      <TableCell className="px-4 py-4 text-sm text-gray-900">
        {formatBookingDate(booking.check_out)}
      </TableCell>

      <TableCell className="px-4 py-4 text-sm text-gray-900">
        <span className="font-medium">{booking.nights}</span>
      </TableCell>

      <TableCell className="px-4 py-4 text-sm text-gray-900">
        <span className="font-medium">
          {formatCurrency(booking.estimated_payout ?? 0)}
        </span>
      </TableCell>

      <TableCell className="px-4 py-4 text-sm">
        {getStayCompletedBadge(booking.stay_completed)}
      </TableCell>

      <TableCell className="px-4 py-4 text-sm">
        {getPaymentStatusBadge(booking.payment_status)}
      </TableCell>

      <TableCell className="px-4 py-4 text-sm">
        {getStatusBadge(booking.status)}
      </TableCell>

      <TableCell className="flex space-x-2">
        <Button variant="outline" size="sm" onClick={() => onEdit(booking)}>
          <Edit className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onCopy(booking.booking_id)}
          title="Copiar enlace de huésped"
        >
          <Copy className="h-4 w-4" />
        </Button>

        {booking.status === 'Reservada' ? (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onCancel(booking)}
            disabled={isLoading}
            className="w-full"
          >
            Cancelar
          </Button>
        ) : (
          <Button
            variant="default"
            size="sm"
            onClick={() => onActivate(booking)}
            disabled={isLoading}
            className="bg-green-600 hover:bg-green-700 w-full"
          >
            Activar
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

export default BookingRow;
